import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import type { JSX } from 'react'
import { AppContainer } from '~/components/new/app-ui/app-container'
import { AppHeader } from '~/components/new/app-ui/app-header'
import { Title } from '~/components/new/app-ui/title'
import { FORECAST_REFERENCE_QUERY_OPTIONS } from '~/lib/forecast-reference-queries'
import { api } from '~/lib/tuyau'
import { withRoutePermissions } from '~/utils/rbac'
import { PrevisionsStoreSelector } from '../previsions-long-terme/-previsions-store-selector'
import { DayChip } from './-day-chip'
import { controleRuptureSearchSchema, yesterday } from './-search-schema'
import { SelectionSummary } from './-selection-summary'
import { StoreTree } from './-store-tree'

export const Route = createFileRoute('/_authenticated/controle-rupture/')({
  validateSearch: controleRuptureSearchSchema,
  staticData: { permission: ['read', 'previsions.controle-rupture.stockout-impact'] },
  component: ControleRupturePage,
})
withRoutePermissions(Route)

function ControleRupturePage(): JSX.Element {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const date = search.date ?? yesterday()

  const setSearch = (next: Partial<typeof search>) =>
    navigate({ search: (prev) => ({ ...prev, ...next }) })

  const impactQuery = useQuery(
    api.forecast.stockoutImpact.queryOptions(
      { query: { storeIds: search.storeIds, date } },
      { enabled: search.storeIds.length > 0 },
    ),
  )

  const { data: forecastStores = [] } = useQuery(
    api.forecast.forecastStores.queryOptions({}, FORECAST_REFERENCE_QUERY_OPTIONS),
  )
  const storeLabels = new Map(forecastStores.map((store) => [store.storeId, store.label]))

  return (
    <>
      <AppHeader>
        <Title>Contrôle des ruptures</Title>
      </AppHeader>
      <AppContainer>
        <div className="flex flex-wrap items-center gap-3">
          <PrevisionsStoreSelector
            selectedIds={search.storeIds}
            onApply={(storeIds) => setSearch({ storeIds })}
            placeholder="Sélectionner des boutiques"
            title="Boutiques"
          />
          <DayChip value={date} onChange={(next) => setSearch({ date: next })} />
        </div>

        {search.storeIds.length === 0 && (
          <p className="text-muted-foreground mt-4">Sélectionne au moins une boutique.</p>
        )}
        {search.storeIds.length > 0 && impactQuery.isPending && (
          <p className="text-muted-foreground mt-4">Chargement…</p>
        )}
        {impactQuery.isError && (
          <p className="mt-4 text-amber-600">Erreur pendant le chargement. Réessaie.</p>
        )}
        {impactQuery.data !== undefined && impactQuery.data.scannedProducts === 0 && (
          <p className="text-muted-foreground mt-4">Aucune donnée pour cette journée.</p>
        )}
        {impactQuery.data !== undefined &&
          impactQuery.data.scannedProducts > 0 &&
          impactQuery.data.totals.productCount === 0 && (
            <p className="text-muted-foreground mt-4">Aucune rupture ce jour-là.</p>
          )}
        {impactQuery.data !== undefined &&
          impactQuery.data.scannedProducts > 0 &&
          impactQuery.data.totals.productCount > 0 && (
            <div className="mt-4">
              <SelectionSummary
                totals={impactQuery.data.totals}
                networkTrendPercent={impactQuery.data.networkTrendPercent}
              />
              <StoreTree stores={impactQuery.data.stores} storeLabels={storeLabels} />
            </div>
          )}
      </AppContainer>
    </>
  )
}
